/* eslint-disable no-param-reassign */
import {
  call,
  put,
  CallEffect,
  PutEffect,
  SelectEffect,
} from 'redux-saga/effects';
import { PayloadAction, AnyAction } from '@reduxjs/toolkit';

import { buildApiUrl } from 'environment';
import fetchHelper, { isOkHttpResponse } from 'services/apiService';
import type { HttpResponse } from 'services/apiService';
import {
  createErrorId,
  createBackendErrorMessage,
  createNetworkErrorMessage,
} from 'services/errorService';
import type { StatusCounts } from 'state/shared/createStatusCounts';
import { countryCodesActions } from 'state/countryCodes';
import { SignUpSteps } from 'enums/signUp';

import type { VerifyAccountResponseBody } from '../../shared/apiTypes';
import { signUpStepsActions } from '../signUpStepsSlice';
import type { VerifyAccountDto } from '../signUpStepsTypes';

const { VERIFY_ACCOUNT_SUCCESS, VERIFY_ACCOUNT_FAILURE, UPDATE_STEP } =
  signUpStepsActions;

const { COUNTRY_CODES_REQUEST } = countryCodesActions;

export default function* verifyAccount(
  statusCounts: StatusCounts,
  action: PayloadAction<VerifyAccountDto>
): Generator<
  CallEffect<AnyAction> | PutEffect<AnyAction> | SelectEffect,
  void,
  HttpResponse<VerifyAccountResponseBody> & number
> {
  const {
    payload: { verificationToken, username },
  } = action;
  const { type: errorActionType } = VERIFY_ACCOUNT_FAILURE;

  yield put(UPDATE_STEP(SignUpSteps.VerifyingAccount));

  try {
    const response = yield call(
      fetchHelper.post,
      buildApiUrl('verifyAccount'),
      { verificationToken, username }
    );
    if (
      isOkHttpResponse<typeof response, VerifyAccountResponseBody>(
        response,
        response.parsedBody
      )
    ) {
      const { username: responseUsername } = response.parsedBody;
      yield put(VERIFY_ACCOUNT_SUCCESS({ username: responseUsername }));
      yield put(UPDATE_STEP(SignUpSteps.SetupPassword));
      yield put(COUNTRY_CODES_REQUEST());
    } else if (response) {
      // backend error
      yield put(
        VERIFY_ACCOUNT_FAILURE({
          id: createErrorId(
            errorActionType,
            (statusCounts[errorActionType] += 1)
          ),
          error: createBackendErrorMessage(response),
        })
      );
    }
  } catch (error) {
    // network error
    if (error instanceof Error) {
      yield put(
        VERIFY_ACCOUNT_FAILURE({
          id: createErrorId(
            errorActionType,
            (statusCounts[errorActionType] += 1)
          ),
          error: createNetworkErrorMessage(error),
        })
      );
    }
  }
}
